import { useMemo, useState } from 'react';
import { useWidgetToken, useWidgetActivated } from '@travelity/api';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Card } from '@travelity/ui/card';
import { useTranslation } from 'react-i18next';
import { copyToClipboard } from '@travelity/utils';

import { List } from '../../components/list-layout';
import { useToast } from '../../hooks';

import { getEmbedContent } from './widget.helpers';

const Widget = () => {
    const { data: widget, isLoading } = useWidgetActivated();
    const { data: tokenData } = useWidgetToken({
        enabled: !!widget?.activated,
    });

    const { t } = useTranslation();
    const { showSuccess } = useToast();

    const [isCopying, setIsCopying] = useState(false);

    const embedString = useMemo(() => {
        return tokenData?.token ? getEmbedContent(tokenData.token) : '';
    }, [tokenData]);

    const isEnabled = Boolean(widget?.activated);

    const handleCopy = async () => {
        if (!embedString) return;

        setIsCopying(true);
        try {
            await copyToClipboard(embedString);
            // Reuse existing toast key pattern. If a translation key isn't present yet,
            // this still shows something meaningful in most setups.
            showSuccess('WIDGET_EMBED_COPIED');
        } catch (error) {
            // copyToClipboard intentionally swallows most errors, but keep this for safety.
            // eslint-disable-next-line no-console
            console.error('Failed to copy widget embed code', error);
        } finally {
            setIsCopying(false);
        }
    };

    return (
        <List>
            <Box
                sx={{
                    flexGrow: 2,
                    mx: 2,
                    my: { xs: 0, sm: 2 },
                    px: 2,
                    height: { xs: '100%', sm: 'calc(100% - 32px)' },
                    minWidth: '320px',
                    overflow: isLoading ? 'hidden' : 'initial',
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    gap={2}
                    py={2}
                >
                    <Card parentProps={{ maxWidth: '520px', width: '100%' }}>
                        <Stack p={2} gap={2}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                            >
                                <Typography fontWeight={600}>
                                    {t('Widget')}
                                </Typography>
                            </Stack>

                            {isEnabled && (
                                <Stack gap={1.5}>
                                    <TextField
                                        label={t('Embed code')}
                                        fullWidth
                                        multiline
                                        minRows={6}
                                        value={embedString}
                                        placeholder={
                                            tokenData
                                                ? ''
                                                : t(
                                                      'Generating widget embed code...'
                                                  )
                                        }
                                        sx={{
                                            // Keep the scroll inside the textarea, not the root wrapper,
                                            // and add padding so the text doesn't touch borders.
                                            '& .MuiInputBase-root': {
                                                alignItems: 'flex-start',
                                            },
                                            '& textarea.MuiInputBase-input': {
                                                maxHeight: '240px',
                                                overflow: 'auto',
                                                boxSizing: 'border-box',
                                                paddingTop: '12px',
                                                paddingBottom: '16px',
                                                paddingLeft: '14px',
                                                paddingRight: '14px',
                                            },
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />

                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="flex-end"
                                        gap={1}
                                    >
                                        <Button
                                            variant="contained"
                                            onClick={handleCopy}
                                            disabled={
                                                isCopying ||
                                                !embedString ||
                                                isLoading
                                            }
                                            startIcon={<ContentCopyIcon />}
                                        >
                                            {t('Copy')}
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </Card>
                </Stack>
            </Box>
        </List>
    );
};

export default Widget;
